/* global HTMLAuxPopoverElement */
import { AuxButton, AuxIcon, AuxPopover, AuxSelect } from "@blk/aladdin-react-components-es";
import { AuxButtonTypeEnum, AuxButtonSizeEnum } from "@blk/aladdin-web-components";
import { isEqual } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { TopFilterGroup } from "components/config/grid-templates/predefined-filters";
import { language } from "language/default";
import { saveState, useGetGridGroups } from "store/grids";
const InsGridGroupSelector = (props) => {
    const dispatch = useDispatch();
    const groups = useGetGridGroups(props.stateId, props.cols);
    let columnState = useSelector((state) => state.grids.state[props.stateId]?.columns, isEqual) ||
        groups.map((e, index) => ({ colId: e, rowGroup: true, rowGroupIndex: index }));
    if (props.index >= props.dimensions.length) {
        return React.createElement(React.Fragment, null);
    }
    const values = props.dimensions.filter((c) => !groups?.includes(c.value) || props.value === c.value)
        .map((d) => ({ displayValue: d.label, isSelected: d.value === props.value, value: d.value }));
    const handleRemoveGroup = () => {
        const removedGroupIndex = columnState?.find((x) => x.colId === props?.value)?.rowGroupIndex || 0;
        dispatch(saveState({
            columns: columnState.map((col) => {
                if (col.colId === props.value) {
                    return { ...col, rowGroup: false, rowGroupIndex: undefined };
                }
                return {
                    ...col,
                    rowGroupIndex: col?.rowGroupIndex && col?.rowGroupIndex > removedGroupIndex ? col?.rowGroupIndex - 1 : col?.rowGroupIndex
                };
            }),
            id: props.stateId
        }));
    };
    const handleReplaceGroup = (currentValue, newValue, currentGroupIndex) => {
        columnState = columnState.map((col) => (col.colId === currentValue ? { ...col, rowGroup: false, rowGroupIndex: undefined } : col));
        const targetRowGroupIndex = currentGroupIndex === undefined ? props.index : currentGroupIndex;
        const colIndex = columnState.findIndex((col) => col.colId === newValue);
        const updatedCol = {
            colId: newValue,
            rowGroup: true,
            rowGroupIndex: targetRowGroupIndex
        };
        if (colIndex === -1) {
            columnState = [...columnState, updatedCol];
        }
        else {
            columnState = columnState.map((col) => (col.colId === newValue
                ? { ...col, rowGroup: true, rowGroupIndex: targetRowGroupIndex }
                : col));
        }
    };
    const handleAddNewGroup = (newValue) => {
        const colIndex = columnState.findIndex((col) => col.colId === newValue);
        const existingGroupIndexes = columnState
            .filter((col) => col.rowGroup === true && typeof col.rowGroupIndex === "number" && col.rowGroupIndex >= 0)
            .map((col) => col.rowGroupIndex);
        const maxGroupIndex = existingGroupIndexes.length > 0 ? Math.max(...existingGroupIndexes) : -1;
        const newRowGroupIndex = maxGroupIndex + 1;
        const updatedCol = {
            colId: newValue,
            rowGroup: true,
            rowGroupIndex: newRowGroupIndex
        };
        if (colIndex === -1) {
            columnState = [...columnState, updatedCol];
        }
        else {
            columnState = columnState.map((col) => (col.colId === newValue
                ? { ...col, rowGroup: true, rowGroupIndex: newRowGroupIndex }
                : col));
        }
    };
    const handleSelectionChange = (e) => {
        const currentValue = props.value;
        const newValue = e.detail.value?.value;
        if (currentValue) {
            const currentGroupIndex = columnState?.find((col) => col.colId === currentValue)?.rowGroupIndex ?? undefined;
            if (newValue) {
                handleReplaceGroup(currentValue, newValue, currentGroupIndex);
            }
        }
        else if (newValue) {
            handleAddNewGroup(newValue);
        }
        dispatch(saveState({ columns: columnState, id: props.stateId }));
    };
    return React.createElement("div", { className: "gridGroupSelector" },
        props.value && React.createElement(AuxIcon, { className: "removeGroupIcon", type: "delete-trash", state: "primary", onClick: handleRemoveGroup }),
        React.createElement(AuxSelect, { style: { fontSize: "bold" }, label: props.index ? language.en.grid.thenBy : language.en.grid.groupBy, data: [{ values }], isLeftLabel: true, hasInitialOptionPlaceholder: true, onSelectionChanged: handleSelectionChange }));
};
const InsGridGroupList = (props) => {
    const groups = useGetGridGroups(props.stateId, props.cols);
    if (groups.length === 0) {
        return React.createElement("div", { className: "gridNoGroup" }, language.en.grid.noGrouping);
    }
    return React.createElement(React.Fragment, null, groups.map((group, index) => React.createElement(InsGridGroupSelector, { cols: props.cols, stateId: props.stateId, key: index, index: index, value: group, dimensions: props.dimensions })));
};
const getColumnDefsWithEnableRowGroup = (columnDefs) => {
    const dimensions = [];
    const getDimensions = (columns) => {
        if (columns) {
            columns.forEach((columnDef) => {
                const def = columnDef, groupDef = columnDef;
                if (def && def.enableRowGroup) {
                    const tempDimension = {
                        label: def.headerGroupName || def.headerName || def.field || "",
                        rowGroup: Boolean(def.rowGroup),
                        value: def.colId || def.field || ""
                    };
                    dimensions.push(tempDimension);
                }
                if (groupDef && groupDef.children && groupDef.children.length) {
                    getDimensions(groupDef.children);
                }
            });
        }
    };
    getDimensions(columnDefs);
    return dimensions;
};
export const InsGridGroupingButton = (props) => {
    // Workaround to take into consideration filtering at column level.
    const groupsExpectFilteringColumn = useGetGridGroups(props.stateId, props.columnDefs)?.filter((d) => d !== TopFilterGroup.customGroup);
    const groupingDimensionsExpectFilteringColumn = getColumnDefsWithEnableRowGroup(props.columnDefs)?.filter((d) => d.value !== TopFilterGroup.customGroup);
    if (!groupingDimensionsExpectFilteringColumn.length) {
        return React.createElement(React.Fragment, null);
    }
    return React.createElement(AuxPopover, { trigger: "pressed", ref: props.popoverRef },
        React.createElement(AuxButton, { slot: "target", label: groupsExpectFilteringColumn.length === 0 ? language.en.grid.group : language.en.grid.grouped(groupsExpectFilteringColumn.length), icon: "arrow-dropdown", isIconRight: true, type: AuxButtonTypeEnum.SECONDARY, size: props.small ? AuxButtonSizeEnum.SMALL : AuxButtonSizeEnum.REGULAR }),
        React.createElement("div", { slot: "content" },
            React.createElement(InsGridGroupList, { stateId: props.stateId, cols: props.columnDefs, dimensions: groupingDimensionsExpectFilteringColumn }),
            React.createElement(InsGridGroupSelector, { stateId: props.stateId, cols: props.columnDefs, index: groupsExpectFilteringColumn.length, dimensions: groupingDimensionsExpectFilteringColumn })));
};
